"use client"; // 错误边界必须是客户端组件

import { monitor } from "@/lib/sentry/instrument";
import { AlertTriangle } from "lucide-react";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

const AUTO_REFRESH_SECONDS = 5;

export function AppErrorView( { error }: { error: Error & { digest?: string } } ) {
    const pathname = usePathname();
    const [ secondsLeft, setSecondsLeft ] = useState( AUTO_REFRESH_SECONDS );
    const shouldShowBackHome = pathname !== "/";

    const handleRefresh = useCallback( () => {
        setSecondsLeft( AUTO_REFRESH_SECONDS );
        window.location.reload();
    }, [] );

    useEffect( () => {
        const timer = setInterval( () => {
            setSecondsLeft( ( prev ) => Math.max( prev - 1, 0 ) );
        }, 1000 );

        return () => clearInterval( timer );
    }, [] );

    useEffect( () => {
        if ( secondsLeft !== 0 ) return;
        handleRefresh();
    }, [ handleRefresh, secondsLeft ] );

    return (
        <div className="h-full min-h-[400px] flex flex-col items-center justify-center py-10 px-5 text-center">
            <AlertTriangle className="w-12 h-12 text-state-danger mb-4" />

            <h2 className="text-lg font-semibold text-t1 mb-2">Something went wrong</h2>

            <p className="text-xs text-t3 mb-6">Auto refresh in {secondsLeft}s</p>

            <div className="flex flex-col gap-3">
                <button
                    // 手动触发刷新恢复
                    onClick={handleRefresh}
                    className="px-5 py-2.5 bg-action-accent text-content-fixed-inverse rounded-lg text-sm font-medium hover:bg-[#08B67C] active:bg-[#079F6D] transition-colors"
                >
                    Refresh now
                </button>

                {shouldShowBackHome && (
                    <button
                        onClick={() => {
                            // 错误边界态下 Next Router 可能已经不可靠，这里用浏览器级跳转强制回首页。
                            window.location.href = "/";
                        }}
                        className="px-5 py-2.5 bg-surface-card text-action-accent border border-action-accent rounded-lg text-sm font-medium hover:bg-[#F0FDF7] active:bg-[#DCFCEB] transition-colors"
                    >
                        Back to home
                    </button>
                )}
            </div>
        </div>
    );
}

export default function Error( { error }: { error: Error & { digest?: string } } ) {
    const pathname = usePathname();

    useEffect( () => {
        // 路由级错误边界负责上报，展示组件本身不再重复打点。
        monitor.error( error, {
            tags: { error_type: "render", error_kind: "route_error_boundary" },
            contextName: "route_error",
            extra: { digest: error.digest, pathname },
        } );
    }, [ error, pathname ] );
    // 复用同一份错误 UI，避免 route error 和 global error 展示两套风格。
    return <AppErrorView error={error} />;
}
